import React from "react";
import { motion } from "framer-motion";
import {
  BadgeCheck,
  PhoneCall,
  Briefcase,
  Users,
  Clock3,
  HeartHandshake,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

const items = [
  { title: "Trusted Employers", icon: BadgeCheck, tone: "sky" },
  { title: "Easy Contact Process", icon: PhoneCall, tone: "orange" },
  { title: "Many Types of Jobs", icon: Briefcase, tone: "indigo" },
  { title: "We Help You Find Work", icon: HeartHandshake, tone: "sky" },
  { title: "Quick Response", icon: Clock3, tone: "orange" },
  { title: "Jobs for Homes & Offices", icon: Users, tone: "indigo" },
];

const toneStyles = {
  sky: {
    iconWrap:
      "bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-100 text-sky-700",
    glow: "from-sky-100/70 via-transparent to-cyan-100/50",
    chip: "bg-sky-50 text-sky-700 border-sky-100",
  },
  orange: {
    iconWrap:
      "bg-gradient-to-br from-orange-50 to-amber-50 border-orange-100 text-orange-700",
    glow: "from-orange-100/70 via-transparent to-amber-100/50",
    chip: "bg-orange-50 text-orange-700 border-orange-100",
  },
  indigo: {
    iconWrap:
      "bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-100 text-indigo-700",
    glow: "from-indigo-100/70 via-transparent to-sky-100/50",
    chip: "bg-indigo-50 text-indigo-700 border-indigo-100",
  },
};

const JobsWhyWorkWithUs = () => {
  return (
    <section className="relative px-4 py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#f9fbff_0%,#ffffff_48%,#fffaf5_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_22%,rgba(56,189,248,0.12),transparent_24%),radial-gradient(circle_at_84%_24%,rgba(99,102,241,0.08),transparent_20%),radial-gradient(circle_at_82%_78%,rgba(251,146,60,0.12),transparent_26%)]" />

      {/* Blobs */}
      <div className="absolute top-[-120px] left-[-80px] w-[240px] h-[240px] bg-sky-200/35 rounded-full blur-3xl opacity-70" />
      <div className="absolute bottom-[-120px] right-[-80px] w-[260px] h-[260px] bg-orange-200/30 rounded-full blur-3xl opacity-70" />

      {/* Texture */}
      <div className="absolute inset-0 opacity-[0.05] [background-image:radial-gradient(#0f172a_0.8px,transparent_0.8px)] [background-size:18px_18px]" />

      {/* Top wave */}
      <svg
        className="absolute top-0 left-0 w-full h-[90px] md:h-[120px] opacity-60"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255,255,255,0.68)"
          d="M0,160L60,149.3C120,139,240,117,360,117.3C480,117,600,139,720,144C840,149,960,139,1080,128C1200,117,1320,107,1380,101.3L1440,96L1440,0L0,0Z"
        />
      </svg>

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-sky-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-sky-700 font-medium mb-5 shadow-sm">
            Why Work Through A2Z
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            A Simple & Trusted Way to Find Work
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            We keep the process simple so you can contact us easily and explore work
            opportunities that fit you.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {items.map((item, i) => {
            const Icon = item.icon;
            const tone = toneStyles[item.tone];

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="group relative rounded-[24px] border border-white/70 bg-white/80 backdrop-blur-xl p-5 md:p-6 shadow-[0_12px_35px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_45px_rgba(15,23,42,0.12)] transition-all duration-300 overflow-hidden"
              >
                {/* Hover glow */}
                <div
                  className={`absolute inset-0 rounded-[24px] opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${tone.glow} pointer-events-none`}
                />

                {/* Soft corner glow */}
                <div className="absolute -top-3 -right-3 w-24 h-24 rounded-full bg-white/70 blur-2xl" />

                {/* Dotted art */}
                <div className="absolute top-3 right-3 opacity-[0.10]">
                  <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                    {Array.from({ length: 5 }).map((_, row) =>
                      Array.from({ length: 5 }).map((__, col) => (
                        <circle
                          key={`${row}-${col}`}
                          cx={7 + col * 11}
                          cy={7 + row * 11}
                          r="2"
                          fill="#0f172a"
                        />
                      ))
                    )}
                  </svg>
                </div>

                <div className="relative flex items-start justify-between gap-3 mb-4">
                  <div
                    className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-2xl border ${tone.iconWrap} group-hover:scale-105 transition duration-300`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>

                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-full border ${tone.chip}`}
                  >
                    <Sparkles className="w-4 h-4" />
                  </span>
                </div>

                <h3 className="relative font-semibold text-gray-900 text-sm sm:text-base md:text-lg leading-snug">
                  {item.title}
                </h3>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom info strip */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="mt-12 md:mt-14"
        >
          <div className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white/75 backdrop-blur-xl shadow-[0_12px_35px_rgba(15,23,42,0.06)] px-5 py-5 md:px-6">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(56,189,248,0.10),transparent_24%),radial-gradient(circle_at_82%_80%,rgba(251,146,60,0.10),transparent_24%)]" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-50 to-orange-50 border border-sky-100 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-indigo-700 mb-1">
                    Simple process, real support
                  </p>
                  <p className="text-sm md:text-base text-gray-600">
                    Contact us, share your details, and our team will help you with suitable job opportunities.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-2 rounded-full border border-sky-100 bg-sky-50 text-sky-700 text-sm font-medium">
                  Easy to Apply
                </span>
                <span className="px-3 py-2 rounded-full border border-orange-100 bg-orange-50 text-orange-700 text-sm font-medium">
                  Quick Response
                </span>
                <span className="px-3 py-2 rounded-full border border-indigo-100 bg-indigo-50 text-indigo-700 text-sm font-medium">
                  Trusted Contact
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default JobsWhyWorkWithUs;